// src/pages/ApproveListing.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, deleteDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { createUserWithEmailAndPassword, updateProfile, signInWithEmailAndPassword } from 'firebase/auth';
import { db, auth } from '../firebase';
import { addListing } from '../services/ListingsService';
import { useAuth } from '../contexts/AuthContext';
import '../Navigation.css';

function ApproveListing() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { refreshUserPermissions, currentUser } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [tokenData, setTokenData] = useState(null);
  const [pendingListing, setPendingListing] = useState(null);
  
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
    acceptTerms: false
  });

  // Validate token and fetch pending listing
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError('Invalid approval link');
        setLoading(false);
        return;
      }

      try {
        // Get token data
        const tokenDoc = await getDoc(doc(db, 'approvalTokens', token));
        
        if (!tokenDoc.exists()) {
          setError('Invalid or expired approval link');
          setLoading(false);
          return;
        }

        const tokenInfo = tokenDoc.data();
        
        // Check if token is expired
        if (tokenInfo.expiresAt && tokenInfo.expiresAt.toDate() < new Date()) {
          setError('This approval link has expired. Please contact the administrator for a new one.');
          setLoading(false);
          return;
        }

        // Check if token has been used
        if (tokenInfo.used) {
          setError('This approval link has already been used.');
          setLoading(false);
          return;
        }

        setTokenData({ id: tokenDoc.id, ...tokenInfo });

        if (tokenInfo.listingId) {
          // Current flow: the listing is already live; this link just sets up
          // the account and links ownership
          const listingDoc = await getDoc(doc(db, 'listings', tokenInfo.listingId));

          if (!listingDoc.exists()) {
            setError('The associated listing could not be found.');
            setLoading(false);
            return;
          }

          const listingData = listingDoc.data();
          if (listingData.userId) {
            setError('This listing has already been linked to an account. Please sign in to manage it.');
            setLoading(false);
            return;
          }

          setPendingListing({ id: listingDoc.id, ...listingData, isLive: true });
        } else {
          // Legacy flow: listing is still unpublished in pendingListings
          const listingDoc = await getDoc(doc(db, 'pendingListings', tokenInfo.pendingListingId));

          if (!listingDoc.exists()) {
            setError('The associated listing could not be found.');
            setLoading(false);
            return;
          }

          setPendingListing({ id: listingDoc.id, ...listingDoc.data() });
        }
        setLoading(false);
      } catch (err) {
        console.error('Error validating token:', err);
        setError('An error occurred while validating your approval link.');
        setLoading(false);
      }
    };

    validateToken();
  }, [token]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Shared helper to link (or, for legacy links, publish) the listing and update user profile
  const completeApprovalForUser = async (user) => {
    if (!pendingListing || !tokenData) {
      throw new Error('Missing token or listing data');
    }

    const providerName = pendingListing.providerName || user.displayName || tokenData.email;

    // Ensure display name is set for the provider
    if (!user.displayName && providerName) {
      await updateProfile(user, { displayName: providerName });
    }

    // Upsert user profile with provider role
    const userRef = doc(db, 'users', user.uid);
    const existingUserDoc = await getDoc(userRef);
    const userData = {
      email: tokenData.email,
      displayName: providerName,
      role: 'service_provider',
      status: 'approved',
      platformFeePercentage: 20,
      updatedAt: serverTimestamp()
    };
    if (!existingUserDoc.exists()) {
      userData.createdAt = serverTimestamp();
    }
    await setDoc(userRef, userData, { merge: true });

    if (pendingListing.isLive) {
      // Listing is already published — link ownership to the new account
      await updateDoc(doc(db, 'listings', pendingListing.id), {
        userId: user.uid,
        pendingOwnerEmail: null,
        updatedAt: serverTimestamp()
      });
    } else {
      // Legacy flow: publish the pending listing now
      const listingData = {
        title: pendingListing.title,
        description: pendingListing.description,
        category: pendingListing.category,
        price: pendingListing.price,
        location: pendingListing.location,
        isVirtual: pendingListing.isVirtual,
        imageUrl: pendingListing.imageUrl,
        contact: tokenData.email,
        media: pendingListing.media || [],
        featured: pendingListing.featured || false,
        isFreeEvent: pendingListing.isFreeEvent || false,
        isContactForPrice: pendingListing.isContactForPrice || false,
        maxAttendees: pendingListing.maxAttendees || null,
        eventStart: pendingListing.eventStart || null,
        eventEnd: pendingListing.eventEnd || null,
        locations: pendingListing.locations || []
      };

      await addListing(listingData, user.uid);
    }

    // Mark token as used and clean up any legacy pending listing
    await updateDoc(doc(db, 'approvalTokens', token), {
      used: true,
      usedAt: serverTimestamp(),
      userId: user.uid
    });
    if (!pendingListing.isLive) {
      await deleteDoc(doc(db, 'pendingListings', tokenData.pendingListingId));
    }

    setSuccess(pendingListing.isLive
      ? 'Account created! Your live listing is now linked to your dashboard.'
      : 'Account ready! Your listing is now live.');

    // Refresh user permissions so AuthContext picks up the new role immediately
    await refreshUserPermissions();

    // Redirect to provider dashboard after a short delay
    setTimeout(() => {
      navigate('/provider-dashboard');
    }, 2000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Validate passwords
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    if (!formData.acceptTerms) {
      setError('You must accept the terms and conditions');
      return;
    }

    setLoading(true);

    try {
      // If the user is already signed in with the target email, just complete the approval
      if (
        currentUser &&
        currentUser.email &&
        tokenData?.email &&
        currentUser.email.toLowerCase() === tokenData.email.toLowerCase()
      ) {
        await completeApprovalForUser(currentUser);
        return;
      }

      // Create a new user account
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        tokenData.email,
        formData.password
      );

      await completeApprovalForUser(userCredential.user);
    } catch (err) {
      console.error('Error creating account:', err);
      if (err.code === 'auth/email-already-in-use') {
        // Try signing in with the provided password and completing approval
        try {
          const signInResult = await signInWithEmailAndPassword(auth, tokenData.email, formData.password);
          await completeApprovalForUser(signInResult.user);
          return;
        } catch (signInError) {
          console.error('Error signing in existing account for approval:', signInError);
          if (signInError.code === 'auth/wrong-password') {
            setError('An account with this email already exists. Please sign in with your existing password or reset it, then return to this link to finish linking your listing.');
          } else {
            setError('An account with this email already exists. Please sign in with this email to complete approval.');
          }
        }
      } else {
        setError('Failed to create account. Please try again.');
      }
      setLoading(false);
    }
  };

  if (loading && !tokenData) {
    return (
      <div className="sign-in-container">
        <div className="sign-in-box">
          <h2>Validating approval link...</h2>
        </div>
      </div>
    );
  }

  if (error && !tokenData) {
    return (
      <div className="sign-in-container">
        <div className="sign-in-box">
          <h2>Error</h2>
          <div className="error-message">{error}</div>
          <button onClick={() => navigate('/signin')} className="signin-button">
            Go to Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="sign-in-container">
      <div className="sign-in-box">
        <h2>Complete Your Account Setup</h2>
        
        {pendingListing && (
          <div className="listing-preview" style={{
            backgroundColor: '#f8f9fb',
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '1.5rem',
            borderLeft: '4px solid var(--color-primary)'
          }}>
            <h3 style={{ margin: '0 0 0.5rem 0', color: 'var(--color-primary)' }}>Your Listing: {pendingListing.title}</h3>
            <p style={{ margin: '0.25rem 0' }}>Category: {pendingListing.category.replace('_', ' ')}</p>
            <p style={{ margin: '0.25rem 0' }}>Price: ${pendingListing.price}</p>
            {pendingListing.isLive && (
              <p style={{ margin: '0.25rem 0', fontWeight: 'bold' }}>This listing is already live. Create your account to manage it.</p>
            )}
          </div>
        )}

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={tokenData?.email || ''}
              disabled
              className="disabled-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Create Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              required
              placeholder="Enter your password"
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              required
              placeholder="Confirm your password"
            />
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                name="acceptTerms"
                checked={formData.acceptTerms}
                onChange={handleInputChange}
                required
              />
              I accept the <a href="https://docs.google.com/document/d/1KW3e5nP5n3xAFi5iBMgykfadaB_1TrSf/edit?usp=drive_link&ouid=105347652403814580551&rtpof=true&sd=true" target="_blank" rel="noopener noreferrer">Terms and Conditions</a> and <a href="https://docs.google.com/document/d/1cvex61ZBqv98UACTi7cCDE3GjuY_nILi/edit?usp=drive_link&ouid=105347652403814580551&rtpof=true&sd=true" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
            </label>
          </div>

          <button 
            type="submit" 
            className="signin-button"
            disabled={loading || !formData.acceptTerms}
          >
            {loading ? 'Creating Account...' : (pendingListing?.isLive ? 'Create Account' : 'Create Account & Publish Listing')}
          </button>
        </form>

        <div className="signin-links">
          <p>Already have an account? <a href="/signin">Sign In</a></p>
        </div>
      </div>
    </div>
  );
}

export default ApproveListing;
